import { assert, Logger } from '@deqo/foundation';
import { interfaces } from 'inversify';
import { Activator, Resolver } from '../../app/Container';
import { Host } from '../../Host';
import { Monitor } from '../../platform/Monitor';
import { CommandContext, CommandDefinition } from './CommandDefinition';
import { CommandRegistry } from './CommandRegistry';

type Container = interfaces.Container;

const errLog = Logger.get('app.errors');

const createContext = (scope: Container, dispatch: (nameOrDef: any, input: any) => any): CommandContext => {
    const activator = scope.get(Activator);
    const resolver = scope.get(Resolver);
    return {
        dispatch,
        activate<T>(identifier: interfaces.Newable<T>) {
            return activator.call<T>(identifier);
        },
        resolve<T>(identifier: interfaces.ServiceIdentifier<T>, strict?: boolean) {
            return resolver.call<T>(identifier, strict);
        },
        call<T>(identifier: interfaces.ServiceIdentifier<T>, strict?: boolean) {
            return resolver.call<T>(identifier, strict);
        }
    };
};

const findRoot = (scope: Container): Container => {
    if (!scope.parent) return null;
    while (!!scope.parent) {
        scope = scope.parent;
    }
    return scope;
};

/**
 * Represents a functor that can dispatch commands.  Dispatchers allocate a resolver for the command invocation,
 * perform the invocation and then cleanup the resolver.
 */
export interface CommandDispatcher {
    <P>(def: CommandDefinition<P>, input?: P): unknown;
    (name: string, input?: any[]): unknown;
    parent?: CommandDispatcher;
    root: CommandDispatcher;
}

export const CommandDispatcher = Symbol('CommandDispatcher') as unknown as interfaces.Newable<CommandDispatcher>;

export const createBoundCommandDispatcher = (scope: Container): CommandDispatcher => {
    const registry = scope.get(CommandRegistry);

    // This is weird because we need `dispatch` to create the context, but the context needs `dispatch`...
    const state = {
        depth: 0,
        context: null as CommandContext,
    };

    // Maintain a state object for the current call depth
    const dispatch = (nameOrDef: any, input: any) => {
        try {
            state.depth++;
            // Resolve (if string)
            if (typeof (nameOrDef) === 'string') {
                const name = nameOrDef;
                nameOrDef = registry.get(name);
                assert(nameOrDef, `${name} not a recognized command.`);
            }
            // Invoke
            // return profileBlock(def.name, () => {
            //     const r = def.impl.call(null, resolver, (input ?? []));
            //     return r;
            // });

            return nameOrDef.impl.call(null, state.context, (input ?? []));
        }
        catch (err) {
            // Only catch if we are at the top level, otherwise keep unwinding
            if (state.depth === 1) {
                Monitor.error(`CommandError in ${nameOrDef.name}`, err);
                errLog.error('Command Error:', err);
                if (Host.env !== 'prod') {
                    alert(`Command Error!\n\n${err.message}'\n'${err.stack}`);
                }
                return err;
            }
            else {
                throw err;
            }
        }
        finally {
            state.depth--;
        }
    };

    // Create the context, after the dispatch, so that the dispatch can use the closure scope of state to access the context.
    state.context = createContext(scope, dispatch);

    const parent = scope.parent?.get(CommandDispatcher);
    const root = findRoot(scope)?.get(CommandDispatcher);

    return Object.assign(dispatch, { parent, root });
};

export type CommandInvoker = (d: CommandDispatcher) => unknown;
